import "@/App.css";
import "@/styles/ops-chrome.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SportProvider } from "@/context/SportContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ProgressViewProvider } from "@/context/ProgressViewContext";
import { UserModeProvider } from "@/context/UserModeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import CoachDashboard from "@/pages/CoachDashboard";
import StudentDashboard from "@/pages/StudentDashboard";
import CoachesNearMe from "@/pages/CoachesNearMe";
import AdminPromoCodes from "@/pages/AdminPromoCodes";
import AdminFestivalEnquiries from "@/pages/AdminFestivalEnquiries";
import AdminTeamShareAudit from "@/pages/AdminTeamShareAudit";
import CalendarPage from "@/pages/CalendarPage";
import CoachSchedule from "@/pages/CoachSchedule";
import StudentSchedule from "@/pages/StudentSchedule";

function ScheduleRoute() {
  const { user } = useAuth();
  if (user?.role === "coach") return <CoachSchedule />;
  if (user?.role === "student") return <StudentSchedule />;
  return <CalendarPage />;
}
import Players from "@/pages/Players";
import PlayerDetail from "@/pages/PlayerDetail";
import MyProgress from "@/pages/MyProgress";
import PathwayPrint from "@/pages/PathwayPrint";
import TrophyCase from "@/pages/TrophyCase";
import LevelUpModal from "@/components/LevelUpModal";
import Reports from "@/pages/Reports";
import PricingSettings from "@/pages/PricingSettings";
import Offerings from "@/pages/Offerings";
import OfferingNew from "@/pages/OfferingNew";
import Availability from "@/pages/Availability";
import StudentBookNext from "@/pages/StudentBookNext";
import GroupRequestsList from "@/pages/GroupRequests";
import GroupBookingNew from "@/pages/GroupBookingNew";
import GroupBookingDetail from "@/pages/GroupBookingDetail";
import RequestChooser from "@/pages/RequestChooser";
import NotificationsPage from "@/pages/Notifications";
import CoachesDirectory from "@/pages/CoachesDirectory";
import Requests from "@/pages/Requests";
import CoachApply from "@/pages/CoachApply";
import CoachPending from "@/pages/CoachPending";
import AdminApplications from "@/pages/AdminApplications";
import FeatureSettings from "@/pages/FeatureSettings";
import CoachProfile from "@/pages/CoachProfile";
import CoachFeedbackQueue from "@/pages/CoachFeedbackQueue";
import AdminInbox from "@/pages/AdminInbox";
import PublicCoaches from "@/pages/PublicCoaches";
import PublicTeam from "@/pages/PublicTeam";
import Teams from "@/pages/Teams";
import UserManagement from "@/pages/UserManagement";
import AuditLog from "@/pages/AuditLog";
import AssessmentClaimDetail from "@/pages/AssessmentClaimDetail";
import CoachOnboardingGate from "@/components/CoachOnboardingGate";
import SelfRating from "@/pages/SelfRating";
import MediaConsent from "@/pages/MediaConsent";
import AdminMediaConsent from "@/pages/AdminMediaConsent";
import VerifyEmail, { VerifyEmailByToken, MagicLogin } from "@/pages/VerifyEmail";
import Feed from "@/pages/Feed";
import CoachFollowers from "@/pages/CoachFollowers";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import BookingConfirmed from "@/pages/BookingConfirmed";
import AdminOverview from "@/pages/AdminOverview";
import AdminStats from "@/pages/AdminStats";
import AdminUserDetail from "@/pages/AdminUserDetail";
import AdminStripeConfig from "@/pages/AdminStripeConfig";
import PersonalDetails from "@/pages/PersonalDetails";
import SessionDetail from "@/pages/SessionDetail";
import PayBooking from "@/pages/PayBooking";
import StudentSession from "@/pages/StudentSession";
import MyCredits from "@/pages/MyCredits";
import ChangePassword from "@/pages/ChangePassword";
import CoachPackages from "@/pages/CoachPackages";
import AdminLandingSettings from "@/pages/AdminLandingSettings";
import AdminEmails from "@/pages/AdminEmails";
import AdminEmailDiagnostics from "@/pages/AdminEmailDiagnostics";
import AdminRegions from "@/pages/AdminRegions";
import AdminPendingOffers from "@/pages/AdminPendingOffers";
import DrillLibrary from "@/pages/DrillLibrary";
import DrillDetail from "@/pages/DrillDetail";
import RoutineDetail from "@/pages/RoutineDetail";
// Iter179 — Rally-Lab Festival Live Ops module (siloed from coaching).
import AdminFestivals from "@/pages/AdminFestivals";
import FestivalHome from "@/pages/FestivalHome";
import FestivalLanding from "@/pages/FestivalLanding";
import FestivalNew from "@/pages/FestivalNew";
import FestivalDetail from "@/pages/FestivalDetail";
import FestivalActivation from "@/pages/FestivalActivation";
import FestivalExtensionPaid from "@/pages/FestivalExtensionPaid";
import FestivalPricing from "@/pages/FestivalPricing";
import FestivalPlayers from "@/pages/FestivalPlayers";
import FestivalLiveOps from "@/pages/FestivalLiveOps";
import { PlayerModeSwitch } from "@/components/PlayerModeSwitch";
import FestivalPublicViewer from "@/pages/FestivalPublicViewer";
import FestivalPlayerSelfService from "@/pages/FestivalPlayerSelfService";
import FestivalKiosk from "@/pages/FestivalKiosk";
import FestivalCoachingView from "@/pages/FestivalCoachingView";
import FestivalCourtsOps from "@/pages/FestivalCourtsOps";
import FestivalAnalytics from "@/pages/FestivalAnalytics";
import FestivalHelpers from "@/pages/FestivalHelpers";
import FestivalToolkit from "@/pages/FestivalToolkit";
import FestivalHelperRedeem from "@/pages/FestivalHelperRedeem";
import FestivalTerms from "@/pages/FestivalTerms";
import { NotificationsProvider } from "@/context/NotificationsContext";

function Dashboard() {
  const { user, viewMode } = useAuth();
  const perms = user?.permissions || [user?.role].filter(Boolean);
  const isCoachLike = user?.role === "coach" || user?.role === "admin" || perms.includes("coach") || perms.includes("admin") || perms.includes("super_admin");
  // Dual-role: if admin has enabled student profile AND user has flipped to student mode, render the student dashboard
  if (isCoachLike && user?.dual_role_enabled && viewMode === "student") return <StudentDashboard />;
  if (isCoachLike) return <CoachDashboard />;
  return <StudentDashboard />;
}

function Shell({ children }) {
  return <Layout>{children}</Layout>;
}

// Iter89 — catch-all landing. Signed-in users bounce to /dashboard so a
// broken deep link never feels like a stealth logout; guests still hit the
// public landing where they can sign in / register.
function CatchAllRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? "/dashboard" : "/"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <SportProvider>
          <ProgressViewProvider>
            <UserModeProvider>
              <BrowserRouter>
                <Toaster position="top-right" />
                <PlayerModeSwitch />
                <NotificationsProvider>
                  <LevelUpModal />
                  <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/coaches" element={<PublicCoaches />} />
            <Route path="/coaches/:id" element={<PublicCoaches />} />
            <Route path="/teams/:id" element={<PublicTeam />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/booking/confirmed" element={
              <ProtectedRoute><BookingConfirmed /></ProtectedRoute>
            } />

            <Route path="/dashboard" element={
              <ProtectedRoute><Shell><Dashboard /></Shell></ProtectedRoute>
            } />
            <Route path="/calendar" element={
              <ProtectedRoute><Shell><ScheduleRoute /></Shell></ProtectedRoute>
            } />
            <Route path="/calendar/classic" element={
              <ProtectedRoute><Shell><CalendarPage /></Shell></ProtectedRoute>
            } />
            <Route path="/players" element={
              <ProtectedRoute role="coach"><Shell><Players /></Shell></ProtectedRoute>
            } />
            <Route path="/players/:id" element={
              <ProtectedRoute role="coach"><Shell><PlayerDetail /></Shell></ProtectedRoute>
            } />
            <Route path="/packages" element={
              <ProtectedRoute role="coach"><Shell><CoachPackages /></Shell></ProtectedRoute>
            } />
            <Route path="/progress" element={
              <ProtectedRoute><Shell><MyProgress /></Shell></ProtectedRoute>
            } />
            <Route path="/badges" element={
              <ProtectedRoute><Shell><TrophyCase /></Shell></ProtectedRoute>
            } />
            <Route path="/pathway/print/:studentId" element={
              <ProtectedRoute><PathwayPrint /></ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute><Shell><Reports /></Shell></ProtectedRoute>
            } />
            <Route path="/settings/pricing" element={
              <ProtectedRoute role="coach"><Shell><PricingSettings /></Shell></ProtectedRoute>
            } />
            <Route path="/offerings" element={
              <ProtectedRoute><Shell><Offerings /></Shell></ProtectedRoute>
            } />
            <Route path="/coaches-near-me" element={
              <ProtectedRoute><Shell><CoachesNearMe /></Shell></ProtectedRoute>
            } />
            <Route path="/offerings/new" element={
              <ProtectedRoute role="coach"><Shell><OfferingNew /></Shell></ProtectedRoute>
            } />
            <Route path="/offerings/:id" element={
              <ProtectedRoute><Shell><SessionDetail /></Shell></ProtectedRoute>
            } />
            <Route path="/credits" element={
              <ProtectedRoute><Shell><MyCredits /></Shell></ProtectedRoute>
            } />
            <Route path="/session/:id" element={
              <ProtectedRoute><Shell><StudentSession /></Shell></ProtectedRoute>
            } />
            <Route path="/pay/booking/:id" element={
              <ProtectedRoute><Shell><PayBooking /></Shell></ProtectedRoute>
            } />
            <Route path="/availability" element={
              <ProtectedRoute role="coach"><Shell><Availability /></Shell></ProtectedRoute>
            } />
            <Route path="/book/coach/:coachId" element={
              <ProtectedRoute><Shell><StudentBookNext /></Shell></ProtectedRoute>
            } />
            <Route path="/group-requests" element={
              <ProtectedRoute><Shell><GroupRequestsList /></Shell></ProtectedRoute>
            } />
            <Route path="/group-bookings/new" element={
              <ProtectedRoute><Shell><GroupBookingNew /></Shell></ProtectedRoute>
            } />
            {/* Iter126 — Consolidated request chooser. Solo / Group / Club. */}
            <Route path="/request/new" element={
              <ProtectedRoute><Shell><RequestChooser /></Shell></ProtectedRoute>
            } />
            {/* Iter126 — key={type param} forces GroupBookingNew to remount
                when the user switches categories so per-type defaults
                (max_players, payment_mode, etc.) always apply. */}
            <Route path="/group-bookings/:id" element={
              <ProtectedRoute><Shell><GroupBookingDetail /></Shell></ProtectedRoute>
            } />
            <Route path="/notifications" element={
              <ProtectedRoute><Shell><NotificationsPage /></Shell></ProtectedRoute>
            } />
            <Route path="/find-coach" element={
              <ProtectedRoute><Shell><CoachesDirectory /></Shell></ProtectedRoute>
            } />
            <Route path="/requests" element={
              <ProtectedRoute><Shell><Requests /></Shell></ProtectedRoute>
            } />

            {/* Coach onboarding — accessible to pending/incomplete coaches */}
            <Route path="/coach/feedback" element={
              <ProtectedRoute role="coach"><Shell><CoachFeedbackQueue /></Shell></ProtectedRoute>
            } />
            <Route path="/coach/apply" element={<CoachOnboardingGate><CoachApply /></CoachOnboardingGate>} />
            <Route path="/coach/pending" element={<CoachOnboardingGate><CoachPending /></CoachOnboardingGate>} />

            {/* Iter179 — Rally-Lab Festival Live Ops module. Runs alongside the
                coaching module; tenant/festival scoped on the backend. */}
            {/* Iter182 — Public marketing landing for /festival. Old
                logged-in list moved to /festival/mine. */}
            <Route path="/festival" element={<FestivalLanding />} />
            <Route path="/festival/mine" element={
              <ProtectedRoute><Shell><FestivalHome /></Shell></ProtectedRoute>
            } />
            <Route path="/festival/pricing" element={
              <ProtectedRoute role="admin"><Shell><FestivalPricing /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/festivals" element={
              <ProtectedRoute role="admin"><Shell><AdminFestivals /></Shell></ProtectedRoute>
            } />
            <Route path="/festival/new" element={
              <ProtectedRoute><Shell><FestivalNew /></Shell></ProtectedRoute>
            } />
            <Route path="/festival/extension/paid" element={<FestivalExtensionPaid />} />
            <Route path="/festival/:id/activation" element={
              <ProtectedRoute><Shell><FestivalActivation /></Shell></ProtectedRoute>
            } />
            <Route path="/festival/:id/players" element={
              <ProtectedRoute><Shell><FestivalPlayers /></Shell></ProtectedRoute>
            } />
            <Route path="/festival/:id/live" element={
              <ProtectedRoute><Shell><FestivalLiveOps /></Shell></ProtectedRoute>
            } />
            {/* Public viewer — no auth, no Shell (full-screen big-board layout). */}
            <Route path="/festival/:id/viewer" element={<ErrorBoundary><FestivalPublicViewer /></ErrorBoundary>} />
            <Route path="/coaching/:fid" element={<ErrorBoundary><FestivalCoachingView /></ErrorBoundary>} />
            <Route path="/festival/:id/me/:token" element={<ErrorBoundary><FestivalPlayerSelfService /></ErrorBoundary>} />
            <Route path="/festival/:id/kiosk" element={
              <ProtectedRoute><Shell><FestivalKiosk /></Shell></ProtectedRoute>
            } />
            <Route path="/festival/:id/analytics" element={
              <ProtectedRoute><Shell><FestivalAnalytics /></Shell></ProtectedRoute>
            } />
            {/* Iter235 — Helper Pass admin + public redeem. */}
            <Route path="/festival/:id/toolkit" element={
              <ProtectedRoute><Shell><FestivalToolkit /></Shell></ProtectedRoute>
            } />
            <Route path="/festival/:id/helpers" element={
              <ProtectedRoute><Shell><FestivalHelpers /></Shell></ProtectedRoute>
            } />
            <Route path="/festival/:fid/helper/:passId" element={<ErrorBoundary><FestivalHelperRedeem /></ErrorBoundary>} />
            {/* Iter431 — Courts OPS helper landing page. Panel-only
                 roster view. No auth wrapper — the page reads the
                 helper token from localStorage and everything server-
                 side goes through the `X-Festival-Helper-Token`
                 header. */}
            <Route path="/festival/:fid/courts-ops" element={<ErrorBoundary><FestivalCourtsOps /></ErrorBoundary>} />
            {/* Iter312 — Festival licence T&Cs (public, linked from signup + checkout). */}
            <Route path="/legal/festival-terms" element={<FestivalTerms />} />
            <Route path="/festival/:id" element={
              <ProtectedRoute><Shell><FestivalDetail /></Shell></ProtectedRoute>
            } />

            {/* Mandatory player onboarding self-rating */}
            <Route path="/onboarding/self-rating" element={
              <ProtectedRoute><SelfRating /></ProtectedRoute>
            } />
            <Route path="/onboarding/media-consent" element={
              <ProtectedRoute><MediaConsent /></ProtectedRoute>
            } />
            <Route path="/onboarding/verify-email" element={
              <ProtectedRoute><VerifyEmail /></ProtectedRoute>
            } />
            <Route path="/feed" element={
              <ProtectedRoute><Shell><Feed /></Shell></ProtectedRoute>
            } />
            <Route path="/coach/followers" element={
              <ProtectedRoute role="coach"><Shell><CoachFollowers /></Shell></ProtectedRoute>
            } />
            {/* Public routes — clickable email links land here. VerifyEmailByToken
                requires an authed session (the /auth/verify-email endpoint uses
                Depends(get_current_user)); MagicLogin does NOT — it exchanges
                the token for a fresh session. */}
            <Route path="/verify-email/:token" element={<VerifyEmailByToken />} />
            <Route path="/login-magic/:token" element={<MagicLogin />} />
            <Route path="/onboarding/personal-details" element={
              <ProtectedRoute><PersonalDetails /></ProtectedRoute>
            } />
            <Route path="/profile/personal" element={
              <ProtectedRoute><Shell><PersonalDetails /></Shell></ProtectedRoute>
            } />
            <Route path="/account/password" element={
              <ProtectedRoute><Shell><ChangePassword /></Shell></ProtectedRoute>
            } />

            {/* Admin */}
            <Route path="/admin" element={
              <ProtectedRoute role="admin"><Shell><AdminOverview /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/stats" element={
              <ProtectedRoute role="admin"><Shell><AdminStats /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/stripe" element={
              <ProtectedRoute role="admin"><Shell><AdminStripeConfig /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/users/:id" element={
              <ProtectedRoute role="admin"><Shell><AdminUserDetail /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/applications" element={
              <ProtectedRoute role="admin"><Shell><AdminApplications /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/features" element={
              <ProtectedRoute role="admin"><Shell><FeatureSettings /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/inbox" element={
              <ProtectedRoute role="admin"><Shell><AdminInbox /></Shell></ProtectedRoute>
            } />
            <Route path="/coach/profile" element={
              <ProtectedRoute role="coach"><Shell><CoachProfile /></Shell></ProtectedRoute>
            } />
            <Route path="/teams" element={
              <ProtectedRoute role="coach"><Shell><Teams /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute role="admin"><Shell><UserManagement /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/audit" element={
              <ProtectedRoute role="admin"><Shell><AuditLog /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/landing" element={
              <ProtectedRoute role="admin"><Shell><AdminLandingSettings /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/emails" element={
              <ProtectedRoute role="coach"><Shell><AdminEmails /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/email-diagnostics" element={
              <ProtectedRoute role="admin"><Shell><AdminEmailDiagnostics /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/regions" element={
              <ProtectedRoute role="admin"><Shell><AdminRegions /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/promo-codes" element={
              <ProtectedRoute role="admin"><Shell><AdminPromoCodes /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/festival/enquiries" element={
              <ProtectedRoute role="admin"><Shell><AdminFestivalEnquiries /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/team-share" element={
              <ProtectedRoute role="admin"><Shell><AdminTeamShareAudit /></Shell></ProtectedRoute>
            } />
            <Route path="/admin/pending-offers" element={
              <ProtectedRoute role="admin"><Shell><AdminPendingOffers /></Shell></ProtectedRoute>
            } />
            {/* Legacy alias — earlier notifications persisted this API-shaped path.
                Redirects to the actual admin overview which hosts the pending-players widget. */}
            <Route path="/admin/players/pending" element={<Navigate to="/admin" replace />} />
            <Route path="/admin/media-consent" element={
              <ProtectedRoute role="admin"><Shell><AdminMediaConsent /></Shell></ProtectedRoute>
            } />
            <Route path="/drills" element={
              <ProtectedRoute><Shell><DrillLibrary /></Shell></ProtectedRoute>
            } />
            <Route path="/drills/:id" element={
              <ProtectedRoute><Shell><DrillDetail /></Shell></ProtectedRoute>
            } />
            <Route path="/routines/:id" element={
              <ProtectedRoute><Shell><RoutineDetail /></Shell></ProtectedRoute>
            } />
            {/* Iter89: assessment-claim deep links from notifications. Both
                singular (detail) and plural (list — reuses dashboard widgets)
                are wired so `/assessment-claims/{id}` from an in-app
                notification lands somewhere sensible. */}
            <Route path="/assessment-claims/:id" element={
              <ProtectedRoute><Shell><AssessmentClaimDetail /></Shell></ProtectedRoute>
            } />
            <Route path="/assessment-claims" element={
              <ProtectedRoute><Shell><StudentDashboard /></Shell></ProtectedRoute>
            } />

            {/* Iter89: signed-in users hitting a broken URL bounce to their
                own dashboard (StudentDashboard / CoachDashboard / etc. render
                behind /dashboard) instead of the marketing landing page —
                that used to feel like a silent logout. Guests keep going to
                the public landing. */}
            <Route path="*" element={<CatchAllRedirect />} />
            </Routes>
          </NotificationsProvider>
              </BrowserRouter>
            </UserModeProvider>
        </ProgressViewProvider>
        </SportProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
